/**
 * ExtractionStatusBadge - Badge per lo stato di estrazione documento
 *
 * Mostra lo stato corrente dell'estrazione con icona e colore appropriati:
 * - pending: grigio con icona clock
 * - processing: blu con spinner animato
 * - extracted/completed: verde con check
 * - failed: rosso con X
 * - manual_entry: viola con icona edit
 */

import { BadgeWithIcon } from "@/components/base/badges/badges";
import {
  Clock,
  Check,
  AlertCircle,
  Edit03,
  Loading01,
} from "@untitledui/icons";
import { ExtractionStatus } from "@/types/api/documents";
import { useTranslation } from "react-i18next";
import { cx } from "@/utils/cx";

interface ExtractionStatusBadgeProps {
  status: ExtractionStatus | string | null | undefined;
  size?: "sm" | "md" | "lg";
  showLabel?: boolean;
  className?: string;
}

// Status configuration with colors and icons
const statusConfig: Record<
  string,
  {
    color: "gray" | "blue" | "success" | "error" | "purple" | "warning";
    icon: typeof Clock;
    labelKey: string;
    animate?: boolean;
  }
> = {
  pending: {
    color: "gray",
    icon: Clock,
    labelKey: "extraction.status.pending",
  },
  processing: {
    color: "blue",
    icon: Loading01,
    labelKey: "extraction.status.processing",
    animate: true,
  },
  extracted: {
    color: "success",
    icon: Check,
    labelKey: "extraction.status.extracted",
  },
  completed: {
    color: "success",
    icon: Check,
    labelKey: "extraction.status.completed",
  },
  failed: {
    color: "error",
    icon: AlertCircle,
    labelKey: "extraction.status.failed",
  },
  manual_entry: {
    color: "purple",
    icon: Edit03,
    labelKey: "extraction.status.manual_entry",
  },
  partially_completed: {
    color: "warning",
    icon: AlertCircle,
    labelKey: "extraction.status.partially_completed",
  },
};

// Fallback translations if i18n keys are not set
const fallbackLabels: Record<string, string> = {
  pending: "In attesa",
  processing: "Elaborazione...",
  extracted: "Completato",
  completed: "Completato",
  failed: "Errore",
  manual_entry: "Manuale",
  partially_completed: "Parziale",
};

export function ExtractionStatusBadge({
  status,
  size = "sm",
  showLabel = true,
  className,
}: ExtractionStatusBadgeProps) {
  const { t } = useTranslation("pages:documents");

  // Normalize status to lowercase string
  const normalizedStatus = status?.toLowerCase() ?? "pending";

  // Get config or use default
  const config = statusConfig[normalizedStatus] ?? statusConfig.pending;

  // Get label with fallback
  const label = t(config.labelKey, fallbackLabels[normalizedStatus] ?? normalizedStatus);

  // Create animated icon component for processing state
  const IconComponent = config.icon;

  return (
    <BadgeWithIcon
      type="pill-color"
      size={size}
      color={config.color}
      iconLeading={(props) => (
        <IconComponent
          {...props}
          className={cx(
            props.className,
            config.animate && "animate-spin"
          )}
        />
      )}
      className={className}
    >
      {showLabel && label}
    </BadgeWithIcon>
  );
}

/**
 * Compact version - only shows icon, no label
 * Useful for table cells or tight spaces
 */
export function ExtractionStatusIcon({
  status,
  className,
}: {
  status: ExtractionStatus | string | null | undefined;
  className?: string;
}) {
  return (
    <ExtractionStatusBadge
      status={status}
      size="sm"
      showLabel={false}
      className={className}
    />
  );
}
